import React, { useState, useEffect, useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import { Search } from 'lucide-react';
import FAQAccordion, { FAQItem } from './FAQAccordion';
import AdSenseInArticle from './AdSenseInArticle';

interface BlogPost {
  title: string;
  slug: string;
  category: string[];
  content: string;
  finalUnsplashImageUrl?: string;
  coverImage?: string;
  createdAt: string;
  // GEO/SEO enrichment fields (populated by normalize script)
  summary?: string[];
  faqs?: FAQItem[];
  wordCount?: number;
}

interface BlogsProps {
  selectedSlug?: string | null;
  setSelectedSlug?: (slug: string | null) => void;
  setPage?: (page: string) => void;
}

// Helper to strip markdown and create clean excerpts for catalog cards
const getExcerpt = (markdown: string, limit = 110) => {
  if (!markdown) return '';
  const plainText = markdown
    .replace(/^#+\s+/gm, '') // Remove headers
    .replace(/\*\*|__/g, '') // Remove bold tags
    .replace(/\*|_/g, '') // Remove italics
    .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1') // Remove links
    .replace(/[-\*]\s+/g, '') // Remove lists
    .replace(/---\r?\n/g, '') // Remove dividers
    .trim();
  
  if (plainText.length <= limit) return plainText;
  return plainText.substring(0, limit) + '...';
};

const getCoverImage = (post: BlogPost) => {
  if (post.coverImage) return post.coverImage;
  if (post.finalUnsplashImageUrl) return post.finalUnsplashImageUrl;
  
  const slug = (post.slug || '').toLowerCase();
  if (slug.includes('menu') || slug.includes('restaurant')) {
    return 'https://images.unsplash.com/photo-1555396273-367ea4eb4db5?q=80&w=600&auto=format&fit=crop';
  }
  if (slug.includes('wifi') || slug.includes('hotspot')) {
    return 'https://images.unsplash.com/photo-1543269865-cbf427effbad?q=80&w=600&auto=format&fit=crop';
  }
  if (slug.includes('business') || slug.includes('vcard') || slug.includes('marketing') || slug.includes('grow')) {
    return 'https://images.unsplash.com/photo-1441986300917-64674bd600d8?q=80&w=600&auto=format&fit=crop';
  }
  if (slug.includes('whatsapp') || slug.includes('chat')) {
    return 'https://images.unsplash.com/photo-1614741118887-7a4ee193a5fa?q=80&w=600&auto=format&fit=crop';
  }
  return 'https://images.unsplash.com/photo-1618005182384-a83a8bd57fbe?q=80&w=600&auto=format&fit=crop';
};

export default function Blogs({ 
  selectedSlug: propSelectedSlug, 
  setSelectedSlug: propSetSelectedSlug,
  setPage
}: BlogsProps = {}) {
  const [blogs, setBlogs] = useState<BlogPost[]>([]);
  const [internalSelectedSlug, setInternalSelectedSlug] = useState<string | null>(null);
  const selectedSlug = propSelectedSlug !== undefined ? propSelectedSlug : internalSelectedSlug;
  const setSelectedSlug = propSetSelectedSlug !== undefined ? propSetSelectedSlug : setInternalSelectedSlug;
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeFilter, setActiveFilter] = useState('All');

  useEffect(() => {
    async function fetchBlogs() {
      try {
        const url = 'https://firestore.googleapis.com/v1/projects/getqrpost-7788e/databases/(default)/documents/blogs';
        const res = await fetch(url);
        
        if (!res.ok) {
          throw new Error('Failed to retrieve articles from the database.');
        }
        
        const data = await res.json();
        
        if (data.documents && data.documents.length > 0) {
          const formatted: BlogPost[] = data.documents.map((doc: any) => {
            const fields = doc.fields;

            // Parse summary array from Firestore (arrayValue)
            const summaryArr: string[] = fields.summary?.arrayValue?.values?.map((v: any) => v.stringValue).filter(Boolean) || [];

            // Parse faqs array from Firestore (arrayValue of mapValues)
            const faqsArr: FAQItem[] = fields.faqs?.arrayValue?.values?.map((v: any) => ({
              question: v.mapValue?.fields?.question?.stringValue || '',
              answer: v.mapValue?.fields?.answer?.stringValue || ''
            })).filter((f: FAQItem) => f.question && f.answer) || [];

            return {
              title: fields.title?.stringValue || '',
              slug: fields.slug?.stringValue || '',
              category: fields.category?.arrayValue?.values?.map((v: any) => v.stringValue) || ['Guides'],
              content: fields.content?.stringValue || '',
              finalUnsplashImageUrl: fields.finalUnsplashImageUrl?.stringValue || '',
              coverImage: fields.coverImage?.stringValue || '',
              createdAt: fields.createdAt?.timestampValue || new Date().toISOString(),
              summary: summaryArr.length > 0 ? summaryArr : undefined,
              faqs: faqsArr.length > 0 ? faqsArr : undefined,
              wordCount: fields.wordCount?.integerValue ? parseInt(fields.wordCount.integerValue) : undefined
            };
          });
          
          // Sort by createdAt descending to always show latest guides first
          formatted.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
          
          setBlogs(formatted);
        } else {
          setBlogs([]);
        }
      } catch (err: any) {
        console.error('Firestore fetch failed:', err);
        setError(err.message || 'Unable to load guides at this time.');
      } finally {
        setLoading(false);
      }
    }
    
    fetchBlogs();
  }, []);

  const selectedPost = blogs.find(post => post.slug === selectedSlug);

  // Derive related articles (excluding the active reading article)
  const relatedArticles = blogs
    .filter(post => post.slug !== selectedSlug)
    .slice(0, 3);

  // Calculate reading time dynamically based on word count
  const calculateReadingTime = (text: string) => {
    if (!text) return 1;
    const words = text.trim().split(/\s+/).length;
    return Math.max(1, Math.ceil(words / 200));
  };

  // Generate strategic TL;DR key takeaways based on marketing search topics
  const getKeyTakeaways = (slug: string, content: string) => {
    if (slug.includes('wifi')) {
      return [
        'Secure guest hotspot connectivity in seconds without spelling passwords.',
        'Client-side QR compiler ensures credentials remain 100% private in browser.',
        'High-resolution vector outputs optimized for table tents and menu prints.'
      ];
    } else if (slug.includes('whatsapp')) {
      return [
        'Initiate instant customer support chats without pre-saving phone numbers.',
        'Supports pre-filled inquiries to streamline customer service channels.',
        'Watermark-free QR compilation protects professional business credibility.'
      ];
    } else if (slug.includes('menu')) {
      return [
        'Seamless contactless PDF redirects with zero intermediate landing page ads.',
        'Free custom branding using center logo overlays and color hex options.',
        'Unlimited scans without monthly subscription or account setups.'
      ];
    }
    return [
      '100% free vector and image generation with no email sign-ups required.',
      'Saves styling parameters inside local cache for fast client-side privacy.',
      'Instant real-time preview matches custom gradients and templates instantly.'
    ];
  };

  // Generate direct, simple English QA lists tailored for target keywords
  const getFaqItems = (slug: string): FAQItem[] => {
    if (slug.includes('wifi')) {
      return [
        {
          question: "Is this Wi-Fi QR code generator completely free?",
          answer: "Yes, our Wi-Fi QR code designer is 100% free with no monthly subscription, scan limits, or account registration required. All generated codes are permanent and work forever."
        },
        {
          question: "Is my Wi-Fi password safe when creating a QR code?",
          answer: "Absolutely. All QR compilation runs 100% locally inside your browser cache using advanced client-side processing. Your network SSID and security password are never uploaded to our servers, keeping your local system completely safe."
        },
        {
          question: "How do guest users scan the Wi-Fi QR code to connect?",
          answer: "Guests can simply point their smartphone camera at the generated QR code. Modern Android and iOS devices automatically recognize the network credentials and prompt users to join in a single tap."
        },
        {
          question: "What download formats do you offer for Wi-Fi codes?",
          answer: "We support standard PNG downloads for easy web and digital sharing, alongside high-quality vector SVG file exports which allow you to scale and print clear signs at any size."
        }
      ];
    } else if (slug.includes('whatsapp')) {
      return [
        {
          question: "Can I generate a WhatsApp QR code without a watermark?",
          answer: "Yes. Unlike competing services, our generator does not inject any watermarks or platform overlays on your final QR images. Your downloads are 100% clean and look highly professional."
        },
        {
          question: "Do users have to save my phone number to message me?",
          answer: "No, that is the core benefit. Scanning the generated QR code instantly redirects users to initiate a secure WhatsApp chat with your target business account without requiring them to pre-save your contact."
        },
        {
          question: "Can I include a pre-filled text query in the QR code?",
          answer: "Yes, you can draft an optional default greeting or support inquiry message. The generator compiles the text, and when a user scans the code, the message is pre-typed inside their input chat box."
        },
        {
          question: "Will the generated WhatsApp link ever expire?",
          answer: "Never. The QR code encodes a static link that points directly to WhatsApp's API. It has no expiration dates or redirect intermediate servers, so your codes will remain functional indefinitely."
        }
      ];
    } else if (slug.includes('menu')) {
      return [
        {
          question: "How do I create a free QR code for my restaurant menu?",
          answer: "First, upload your restaurant menu PDF to your business website or a public cloud drive. Copy the direct URL link, paste it into our Link generator tab, and instantly download your print-ready file."
        },
        {
          question: "Are there any scan limits or fees for digital menus?",
          answer: "None. We believe in providing 100% free digital tools. There are zero monthly platform subscription plans, zero hidden transaction fees, and completely unlimited static scans."
        },
        {
          question: "Can I customize the menu QR code to match my restaurant's style?",
          answer: "Yes. Our customization panel enables you to select custom gradients, choose between square or sleek rounded dots, and upload your central business logo overlay to match your branding."
        },
        {
          question: "Which file format should I choose for menu prints?",
          answer: "We strongly recommend downloading the vector SVG option. It compiles in infinite high-quality detail, ensuring your QR codes print extremely crisp on table tents, menus, entry boards, and banners."
        }
      ];
    }
    
    return [
      {
        question: "How does a client-side QR generator protect my data?",
        answer: "By processing all compilations locally in JavaScript, your raw link data, passwords, and numbers are never transmitted over the internet to external database servers. This keeps your personal information 100% private."
      },
      {
        question: "Do you support vector exports for professional print shops?",
        answer: "Yes, we support both standard raster PNG downloads and crisp vector SVG formats. SVGs can be scaled endlessly without pixelation or scanning issues, making them perfect for commercial printing."
      },
      {
        question: "Can I add my business logo to the center of a QR code?",
        answer: "Absolutely. Our platform lets you upload custom logos (PNG/JPG/SVG) which are centered and clipped. High error correction parameters are automatically applied to ensure the code remains fully readable."
      }
    ];
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-20 gap-4">
        <div className="h-10 w-10 border-4 border-indigo-500 border-t-transparent rounded-full animate-spin"></div>
        <span className="text-gray-400 font-medium text-sm">Loading GetQRPost articles...</span>
      </div>
    );
  }

  if (error && blogs.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-center gap-4">
        <div className="text-red-400 text-lg font-semibold">Unable to Load Guides</div>
        <p className="text-gray-400 max-w-md text-sm">{error}</p>
      </div>
    );
  }

  if (selectedPost) {
    // Split markdown article content at the nearest section header (## ) closest to the midpoint
    const splitContent = (text: string) => {
      if (!text) return { part1: '', part2: '' };
      const parts = text.split(/(?=##\s+)/);
      if (parts.length <= 2) return { part1: text, part2: '' };
      
      const mid = Math.ceil(parts.length / 2);
      return {
        part1: parts.slice(0, mid).join(''),
        part2: parts.slice(mid).join('')
      };
    };

    const { part1, part2 } = splitContent(selectedPost.content);

    return (
      <div className="w-full max-w-3xl mx-auto px-4 py-8 flex flex-col gap-6 font-sans">
        {/* Navigation back button */}
        <button
          onClick={() => {
            setSelectedSlug(null);
            window.scrollTo({ top: 0, behavior: 'smooth' });
          }}
          className="self-start px-4 py-2 rounded-lg bg-gray-900 border border-gray-800 text-gray-400 hover:text-gray-200 text-xs font-semibold uppercase tracking-wider transition-all duration-200"
        >
          &larr; Back to all articles
        </button>

        {/* semantic H1 Title */}
        <h1 className="text-4xl md:text-5xl font-extrabold text-gray-100 tracking-tight mb-2 leading-tight">
          {selectedPost.title}
        </h1>

        {/* Article Meta details */}
        <div className="flex flex-wrap items-center gap-3 text-xs text-gray-500 font-semibold mb-2">
          <span>By GetQRPost Crew</span>
          <span>&bull;</span>
          <span>{new Date(selectedPost.createdAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}</span>
        </div>

        {/* Feature Image directly below H1 */}
        <div className="w-full h-64 md:h-96 rounded-2xl overflow-hidden border border-gray-900 shadow-2xl mb-4 relative">
          <img 
            src={getCoverImage(selectedPost)} 
            alt={selectedPost.title} 
            className="w-full h-full object-cover" 
          />
          <div className="absolute inset-0 bg-gradient-to-t from-gray-950/40 via-transparent to-transparent"></div>
        </div>

        {/* Reading Time & Key Takeaways Summary Box */}
        <div className="bg-gray-900/40 border border-gray-900 rounded-2xl p-6 flex flex-col gap-4 backdrop-blur-sm shadow-md mb-4 select-none">
          <div className="flex flex-wrap items-center justify-between gap-4 border-b border-gray-900/80 pb-4">
            <span className="text-xs uppercase font-bold text-gray-500 tracking-wider">Quick Summary</span>
            <div className="flex items-center gap-1.5 text-xs text-indigo-400 font-semibold">
              <svg className="h-4 w-4 text-indigo-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
              <span>{calculateReadingTime(selectedPost.content)} Min Read</span>
            </div>
          </div>
          
          <div className="flex flex-col gap-3">
            <h4 className="text-xs font-black uppercase text-gray-200 tracking-wide">Key Takeaways (TL;DR)</h4>
            <ul className="flex flex-col gap-2.5">
              {(selectedPost.summary || getKeyTakeaways(selectedPost.slug, selectedPost.content)).map((point, i) => (
                <li key={i} className="flex gap-2.5 text-sm text-gray-400 leading-relaxed items-start">
                  <span className="h-5 w-5 rounded-full bg-indigo-500/10 border border-indigo-500/20 text-[10px] text-indigo-400 flex items-center justify-center font-bold shrink-0 mt-0.5">
                    {i + 1}
                  </span>
                  <span>{point}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Ad Slot 1: After Quick Summary — high visibility placement */}
        <AdSenseInArticle />

        {/* Premium Markdown content with custom Tailwind class overrides */}
        <article className="prose prose-invert max-w-none px-1 leading-relaxed flex flex-col gap-6">
          <ReactMarkdown
            components={{
              h1: ({ node, ...props }) => <h1 className="text-4xl md:text-5xl font-extrabold text-gray-100 tracking-tight mb-6" {...props} />,
              h2: ({ node, ...props }) => <h2 className="text-2xl md:text-3xl font-bold text-gray-100 mt-10 mb-4 border-b border-gray-900 pb-2" {...props} />,
              h3: ({ node, ...props }) => <h3 className="text-xl font-semibold text-gray-200 mt-6 mb-2" {...props} />,
              p: ({ node, ...props }) => <p className="text-gray-300 text-lg leading-relaxed mb-4 text-justify" {...props} />,
              ul: ({ node, ...props }) => <ul className="list-disc list-inside text-gray-300 text-base flex flex-col gap-2 mb-6" {...props} />,
              ol: ({ node, ...props }) => <ol className="list-decimal list-inside text-gray-300 text-base flex flex-col gap-2 mb-6" {...props} />,
              li: ({ node, ...props }) => <li className="text-gray-300 text-lg" {...props} />,
              strong: ({ node, ...props }) => <strong className="text-indigo-400 font-extrabold" {...props} />,
              a: ({ node, href, ...props }) => {
                const isExternal = href && (href.startsWith('http://') || href.startsWith('https://'));
                return isExternal
                  ? <a href={href} className="text-blue-400 hover:text-blue-300 transition-colors underline decoration-2 underline-offset-4 font-medium" target="_blank" rel="noopener noreferrer" {...props} />
                  : <a href={href} className="text-indigo-400 hover:text-indigo-300 transition-colors underline decoration-2 underline-offset-4 font-medium" {...props} />;
              },
              table: ({ node, ...props }) => <table className="w-full border-collapse border border-gray-900 text-left my-6 text-sm" {...props} />,
              th: ({ node, ...props }) => <th className="border border-gray-900 bg-gray-950 p-3 font-bold text-gray-200" {...props} />,
              td: ({ node, ...props }) => <td className="border border-gray-900 p-3 text-gray-400" {...props} />
            }}
          >
            {part1}
          </ReactMarkdown>
          
          {/* Ad Slot 2: Mid-article — between part1 and part2 */}
          {part2 && <AdSenseInArticle />}
          
          {part2 && (
            <ReactMarkdown
              components={{
                h2: ({ node, ...props }) => <h2 className="text-2xl md:text-3xl font-bold text-gray-100 mt-10 mb-4 border-b border-gray-900 pb-2" {...props} />,
                h3: ({ node, ...props }) => <h3 className="text-xl font-semibold text-gray-200 mt-6 mb-2" {...props} />,
                p: ({ node, ...props }) => <p className="text-gray-300 text-lg leading-relaxed mb-4 text-justify" {...props} />,
                ul: ({ node, ...props }) => <ul className="list-disc list-inside text-gray-300 text-base flex flex-col gap-2 mb-6" {...props} />,
                ol: ({ node, ...props }) => <ol className="list-decimal list-inside text-gray-300 text-base flex flex-col gap-2 mb-6" {...props} />,
                li: ({ node, ...props }) => <li className="text-gray-300 text-lg" {...props} />,
                strong: ({ node, ...props }) => <strong className="text-indigo-400 font-extrabold" {...props} />,
                a: ({ node, href, ...props }) => {
                  const isExternal = href && (href.startsWith('http://') || href.startsWith('https://'));
                  return isExternal
                    ? <a href={href} className="text-blue-400 hover:text-blue-300 transition-colors underline decoration-2 underline-offset-4 font-medium" target="_blank" rel="noopener noreferrer" {...props} />
                    : <a href={href} className="text-indigo-400 hover:text-indigo-300 transition-colors underline decoration-2 underline-offset-4 font-medium" {...props} />;
                },
                table: ({ node, ...props }) => <table className="w-full border-collapse border border-gray-900 text-left my-6 text-sm" {...props} />,
                th: ({ node, ...props }) => <th className="border border-gray-900 bg-gray-950 p-3 font-bold text-gray-200" {...props} />,
                td: ({ node, ...props }) => <td className="border border-gray-900 p-3 text-gray-400" {...props} />
              }}
            >
              {part2}
            </ReactMarkdown>
          )}
        </article>

        {/* Premium Call to Action Section (CTA) */}
        <div className="mt-12 bg-gradient-to-tr from-blue-950/20 via-indigo-950/20 to-purple-950/20 border border-indigo-500/20 rounded-2xl p-8 text-center flex flex-col items-center gap-4 shadow-xl select-none">
          <span className="px-3 py-1 rounded-full bg-indigo-500/10 text-indigo-400 border border-indigo-500/20 text-[10px] uppercase font-bold tracking-wider">
            100% Free Tool
          </span>
          <h3 className="text-2xl font-black text-gray-100">Create Your Custom QR Code in Seconds</h3>
          <p className="text-gray-400 text-sm max-w-md leading-relaxed">
            Generate high-resolution URL, Wi-Fi, WhatsApp, and Email QR codes instantly in your browser with our advanced suite. No sign-ups, no hidden costs.
          </p>
          <button
            onClick={() => {
              setSelectedSlug(null);
              if (setPage) setPage('home');
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }}
            className="mt-2 py-3 px-6 rounded-xl bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 text-white font-bold text-sm shadow-lg shadow-indigo-500/20 hover:shadow-indigo-500/35 transition-all duration-200 active:scale-[0.98]"
          >
            Create Your Free QR Code Now
          </button>
        </div>

        {/* AdSense Placement Slot at the Bottom — replaced with real component */}
        <AdSenseInArticle />

        {/* Dynamic Interactive FAQ Section with JSON-LD structured data injection */}
        {/* Prefer Firestore-stored FAQs; fall back to slug-based generator */}
        <FAQAccordion items={selectedPost.faqs && selectedPost.faqs.length > 0 ? selectedPost.faqs : getFaqItems(selectedPost.slug)} />

        {/* Related Articles Navigation */}
        {relatedArticles.length > 0 && (
          <div className="mt-16 pt-12 border-t border-gray-900 flex flex-col gap-6">
            <h4 className="text-xl font-bold text-gray-200">Read Next</h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {relatedArticles.map((post) => (
                <div
                  key={post.slug}
                  onClick={() => {
                    setSelectedSlug(post.slug);
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  }}
                  className="group cursor-pointer bg-gray-900/40 border border-gray-900 hover:border-indigo-500/40 rounded-2xl overflow-hidden flex flex-col transition-all duration-300 backdrop-blur-sm shadow-lg hover:shadow-xl hover:scale-[1.01]"
                >
                  <div className="h-32 w-full overflow-hidden relative">
                    <img
                      src={getCoverImage(post)}
                      alt={post.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-all duration-500"
                    />
                    <span className="absolute top-3 left-3 bg-indigo-600/90 backdrop-blur-md px-2 py-0.5 rounded text-[8px] uppercase font-bold text-white tracking-wider">
                      {post.category[0]}
                    </span>
                  </div>
                  
                  <div className="p-4 flex-1 flex flex-col justify-between gap-3">
                    <h5 className="text-sm font-bold text-gray-200 group-hover:text-indigo-400 transition-colors duration-200 line-clamp-2 leading-snug">
                      {post.title}
                    </h5>
                    <span className="text-[10px] uppercase font-bold text-indigo-400 tracking-wider flex items-center gap-1">
                      Read Guide &rarr;
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  }

  // Derive unique category list dynamically from all fetched articles
  const allCategories = useMemo(() => {
    const cats = new Set<string>();
    blogs.forEach(p => p.category.forEach(c => { if (c) cats.add(c); }));
    return ['All', ...Array.from(cats).sort()];
  }, [blogs]);

  // Apply search + category filter
  const filteredBlogs = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return blogs.filter(post => {
      const matchesSearch = !q ||
        post.title.toLowerCase().includes(q) ||
        getExcerpt(post.content, 300).toLowerCase().includes(q);
      const matchesFilter = activeFilter === 'All' ||
        post.category.some(c => c === activeFilter);
      return matchesSearch && matchesFilter;
    });
  }, [blogs, searchQuery, activeFilter]);

  return (
    <div className="w-full max-w-4xl mx-auto flex flex-col gap-8">
      {/* Page Header */}
      <div className="text-center max-w-lg mx-auto">
        <h2 className="text-4xl font-extrabold bg-gradient-to-r from-blue-400 to-indigo-400 bg-clip-text text-transparent mb-3">
          Knowledge Base &amp; Guides
        </h2>
        <p className="text-gray-400 text-sm">
          Optimize your business operations and physical setups with our step-by-step custom QR code tutorials.
        </p>
      </div>

      {/* Search Bar */}
      <div className="w-full max-w-xl mx-auto relative">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500 pointer-events-none" />
        <input
          type="text"
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
          placeholder="Search articles..."
          className="w-full pl-11 pr-4 py-3 rounded-xl bg-gray-900/60 border border-gray-800 text-gray-200 placeholder-gray-500 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/50 focus:border-indigo-500/50 transition-all duration-200"
        />
        {searchQuery && (
          <button
            onClick={() => setSearchQuery('')}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300 text-lg leading-none transition-colors"
            aria-label="Clear search"
          >
            ×
          </button>
        )}
      </div>

      {/* Category Filter Pills */}
      {allCategories.length > 1 && (
        <div className="flex flex-wrap gap-2 justify-center px-1">
          {allCategories.map(cat => (
            <button
              key={cat}
              onClick={() => setActiveFilter(cat)}
              className={`rounded-full px-4 py-2 text-sm font-medium transition-all duration-200 border ${
                activeFilter === cat
                  ? 'bg-indigo-600 border-indigo-500 text-white shadow-md shadow-indigo-500/20'
                  : 'bg-gray-900/50 border-gray-800 text-gray-400 hover:border-indigo-500/40 hover:text-gray-200'
              }`}
            >
              {cat}
            </button>
          ))}
        </div>
      )}

      {/* Results count */}
      {(searchQuery || activeFilter !== 'All') && (
        <p className="text-center text-xs text-gray-500 font-medium -mt-4">
          {filteredBlogs.length === 0
            ? 'No articles found matching your search.'
            : `Showing ${filteredBlogs.length} article${filteredBlogs.length !== 1 ? 's' : ''}`}
        </p>
      )}

      {blogs.length === 0 ? (
        <div className="text-center py-10 text-gray-500 text-sm">No articles available.</div>
      ) : filteredBlogs.length === 0 ? (
        /* Empty state */
        <div className="flex flex-col items-center justify-center py-16 gap-4 text-center">
          <div className="h-16 w-16 rounded-2xl bg-gray-900/60 border border-gray-800 flex items-center justify-center">
            <Search className="h-7 w-7 text-gray-600" />
          </div>
          <div>
            <p className="text-gray-300 font-semibold mb-1">No articles found matching your search.</p>
            <p className="text-gray-500 text-sm">Try a different keyword or select a different category.</p>
          </div>
          <button
            onClick={() => { setSearchQuery(''); setActiveFilter('All'); }}
            className="mt-2 px-5 py-2 rounded-xl bg-gray-900 border border-gray-800 text-gray-400 hover:text-gray-200 text-sm font-semibold transition-all duration-200"
          >
            Clear filters
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {filteredBlogs.map((post) => (
            <div
              key={post.slug}
              onClick={() => {
                setSelectedSlug(post.slug);
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }}
              className="group cursor-pointer bg-gray-900/40 border border-gray-900 hover:border-indigo-500/40 rounded-2xl overflow-hidden flex flex-col transition-all duration-300 backdrop-blur-sm shadow-lg hover:shadow-xl hover:scale-[1.01]"
            >
              <div className="h-44 w-full overflow-hidden relative">
                <img
                  src={getCoverImage(post)}
                  alt={post.title}
                  className="w-full h-full object-cover group-hover:scale-105 transition-all duration-500"
                />
                <span className="absolute top-4 left-4 bg-indigo-600/90 backdrop-blur-md px-2.5 py-1 rounded-md text-[10px] uppercase font-bold text-white tracking-wider">
                  {post.category[0]}
                </span>
              </div>
              
              <div className="p-5 flex-1 flex flex-col justify-between gap-4">
                <div className="flex flex-col gap-2">
                  <h3 className="text-lg font-bold text-gray-100 group-hover:text-indigo-400 transition-colors duration-200 leading-snug">
                    {post.title}
                  </h3>
                  <p className="text-gray-400 text-xs leading-relaxed line-clamp-3">
                    {getExcerpt(post.content)}
                  </p>
                </div>

                <div className="flex items-center justify-between mt-2 pt-3 border-t border-gray-900/60">
                  <div className="text-xs text-gray-500 font-semibold uppercase tracking-wider">
                    {new Date(post.createdAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}
                  </div>
                  <span className="text-[10px] uppercase font-bold text-indigo-400 group-hover:text-indigo-300 transition-colors flex items-center gap-1">
                    Read Guide &rarr;
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
